import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import FieldCanvas from "./FieldCanvas";
import { findCoords, isGoal, isPass, isPull, isTurnover } from "./GameEvent";
import { GamePoint } from "./GamePoint";
import GamePointEventList from "./GamePointEventList";
import { getGameEventsGroupedByPoint } from "./ufaApi";

interface Props {
  gameID: string;
  homeTeamId: string;
  awayTeamId: string;
}

export default function PointLevelGranularity({
  gameID,
  homeTeamId,
  awayTeamId,
}: Props) {
  const { data: eventsResponse } = useQuery<any>({
    queryKey: ["gameEvents", gameID],
    enabled: !!gameID,
    queryFn: async () => {
      const res = await fetch(
        `https://www.backend.ufastats.com/api/v1/gameEvents?gameID=${encodeURIComponent(
          gameID || "",
        )}`,
      );
      const json = await res.json();
      return json.data || json || [];
    },
  });

  const eventsGroupedByPoint = useMemo(() => {
    if (!eventsResponse) return [] as any[];
    try {
      return getGameEventsGroupedByPoint(
        eventsResponse,
        homeTeamId,
        awayTeamId,
      );
    } catch (e) {
      console.warn("getGameEventsGroupedByPoint failed", e);
      return [] as any[];
    }
  }, [eventsResponse, homeTeamId, awayTeamId]);

  const [selectedPointIndex, setSelectedPointIndex] = useState<number>(0);
  const [showPulls, setShowPulls] = useState<boolean>(true);
  const [showPasses, setShowPasses] = useState<boolean>(true);
  const [showGoals, setShowGoals] = useState<boolean>(true);
  const [showTurnovers, setShowTurnovers] = useState<boolean>(true);

  const pointList = useMemo(
    () => (Array.isArray(eventsGroupedByPoint) ? eventsGroupedByPoint : []),
    [eventsGroupedByPoint],
  );

  // keep selectedPointIndex in bounds when pointList changes
  useEffect(() => {
    if (!pointList || pointList.length === 0) {
      setSelectedPointIndex(0);
      return;
    }
    if (selectedPointIndex >= pointList.length) {
      setSelectedPointIndex(pointList.length - 1);
    }
  }, [pointList, selectedPointIndex]);

  const { points, connections } = useMemo(() => {
    const pts: any[] = [];
    const conns: any[] = [];
    if (!pointList || pointList.length === 0)
      return { points: pts, connections: conns };
    for (const gp of pointList) {
      if (selectedPointIndex !== -1 && selectedPointIndex !== gp.pointNumber)
        continue;

      const evs = [
        ...(gp.pullingTeamEvents || []),
        ...(gp.receivingTeamEvents || []),
      ];

      evs.forEach((ev: any) => {
        // global visibility filters
        if (!showPulls && isPull(ev)) return;
        if (!showPasses && isPass(ev)) return;
        if (!showGoals && isGoal(ev)) return;
        if (!showTurnovers && isTurnover(ev)) return;

        const { startCoord, endCoord } = findCoords(gp, ev);
        if (!startCoord || !endCoord) return;

        pts.push({
          x: startCoord.x,
          y: startCoord.y,
          role: "start",
          info: {
            role: "throw",
            player: ev.thrower,
            event: ev,
          },
        });

        pts.push({
          x: endCoord.x,
          y: endCoord.y,
          role: "end",
          info: {
            role: "catch",
            player: ev.receiver,
            event: ev,
          },
        });

        conns.push({
          x1: startCoord.x,
          y1: startCoord.y,
          x2: endCoord.x,
          y2: endCoord.y,
          info: { event: ev },
        });
      });
    }

    return { points: pts, connections: conns };
  }, [
    pointList,
    selectedPointIndex,
    showPulls,
    showPasses,
    showGoals,
    showTurnovers,
  ]);

  const [hovered, setHovered] = useState<null | {
    point: any;
    left: number;
    top: number;
  }>(null);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <IconButton
          aria-label="previous point"
          onClick={() => setSelectedPointIndex((i) => i - 1)}
          disabled={pointList.length === 0 || selectedPointIndex < 0}
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>

        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel id="point-select-label">Point</InputLabel>
          <Select
            labelId="point-select-label"
            value={selectedPointIndex.toString()}
            label="Point"
            onChange={(e: SelectChangeEvent) =>
              setSelectedPointIndex(Number(e.target.value))
            }
          >
            {pointList.length === 0 ? (
              <MenuItem value={0}>No points</MenuItem>
            ) : (
              [
                <MenuItem key="pt-all" value={-1}>
                  All
                </MenuItem>,
                ...pointList.map((gp: any, idx: number) => (
                  <MenuItem key={`pt-${idx}`} value={idx.toString()}>
                    {`Point ${idx + 1} — ${gp.pullingTeam || ""} pulling to ${gp.receivingTeam || ""} (${(gp.pullingTeamEvents?.length || 0) + (gp.receivingTeamEvents?.length || 0)} events)`}
                  </MenuItem>
                )),
              ]
            )}
          </Select>
        </FormControl>

        <IconButton
          aria-label="next point"
          onClick={() =>
            setSelectedPointIndex((i) => Math.min(pointList.length - 1, i + 1))
          }
          disabled={
            pointList.length === 0 || selectedPointIndex >= pointList.length - 1
          }
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>

        {/* toggles moved to right of field */}
      </Box>
      <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ position: "relative", height: 600 }}>
          <FieldCanvas
            points={points}
            connections={connections}
            mode={"point"}
            positionChoice={"both"}
            showArrows={true}
            onHover={(h) => setHovered(h)}
            style={{ width: "400px", height: "100%" }}
          />

          {hovered && hovered.point && (
            <Paper
              elevation={3}
              style={{
                position: "absolute",
                pointerEvents: "none",
                left: hovered.left,
                top: hovered.top,
                zIndex: 10,
              }}
              sx={{ p: 1 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {hovered.point.info?.role?.toUpperCase()}
              </Typography>
              <Typography variant="caption" display="block">
                {hovered.point.info?.player || "unknown"}
              </Typography>
            </Paper>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPulls}
                onChange={(e) => setShowPulls(Boolean(e.target.checked))}
              />
            }
            label="Show Pulls"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPasses}
                onChange={(e) => setShowPasses(Boolean(e.target.checked))}
              />
            }
            label="Show Passes"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showGoals}
                onChange={(e) => setShowGoals(Boolean(e.target.checked))}
              />
            }
            label="Show Goals"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showTurnovers}
                onChange={(e) => setShowTurnovers(Boolean(e.target.checked))}
              />
            }
            label="Show Turnovers"
          />
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {(() => {
          if (selectedPointIndex === -1) {
            return (
              <div>
                {pointList.map((gp: GamePoint, idx: number) => (
                  <Accordion
                    key={gp.pointNumber || idx}
                    TransitionProps={{ unmountOnExit: true }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >{`Point ${idx + 1}`}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >{`${gp.pullingTeam || ""} → ${gp.receivingTeam || ""} (${(gp.pullingTeamEvents?.length || 0) + (gp.receivingTeamEvents?.length || 0)} events)`}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <GamePointEventList gamePoint={gp} />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            );
          }
          const gp = pointList[selectedPointIndex] || pointList[0] || null;
          return <GamePointEventList gamePoint={gp} />;
        })()}
      </Box>
    </Paper>
  );
}
